import { formatArrayToCommaAndString } from '@common/neo.tax';
import {
  Button,
  Checkbox,
  colors,
  ModalActions,
  ModalHeader,
  RadioGroupCards,
  Stack,
  Typography,
} from '@ui-components/neo.tax';
import { ReactComponent as AlarmBellRing } from '@ui-components/neo.tax/dist/icons/alarm-bell-ring.svg';
import { ReactElement, ReactNode, useEffect, useState } from 'react';

import { useRdProjectCountForDetailsUpdate } from '@neo.tax/rd/hooks';
import { RdProjectDetailsUpdateScope } from '@neo.tax/shared';

import { StyledModal } from './regenerate-rd-project-details-modal.styled';

export type RegenerateRdProjectDetailsOptions = {
  includeName: boolean;
  includeDescription: boolean;
  includeEligibilityDetermination: boolean;
  scope: RdProjectDetailsUpdateScope;
};

const DEFAULT_OPTIONS: RegenerateRdProjectDetailsOptions = {
  includeName: true,
  includeDescription: true,
  includeEligibilityDetermination: true,
  scope: RdProjectDetailsUpdateScope.All,
};

const SCOPE_OPTIONS = [
  { id: 'update-details-scope-all', value: RdProjectDetailsUpdateScope.All, label: 'All projects' },
  {
    id: 'update-details-scope-missing-description',
    value: RdProjectDetailsUpdateScope.MissingDescription,
    label: 'Only projects missing a description',
  },
  {
    id: 'update-details-scope-missing-eligibility',
    value: RdProjectDetailsUpdateScope.MissingEligibility,
    label: 'Only projects missing eligibility',
  },
];

const getScopePhrase = (scope: RdProjectDetailsUpdateScope, count: ReactNode): ReactNode => {
  switch (scope) {
    case RdProjectDetailsUpdateScope.MissingDescription:
      return <>the {count} projects missing a description</>;
    case RdProjectDetailsUpdateScope.MissingEligibility:
      return <>the {count} projects missing eligibility</>;
    case RdProjectDetailsUpdateScope.All:
      return <>all {count} projects with tickets or documents, excluding noise projects,</>;
  }
};

export const RegenerateRdProjectDetailsModal = ({
  fiscalYearId,
  fiscalYearLabel,
  isOpen,
  loading,
  onSubmit,
  setIsOpen,
}: {
  fiscalYearId: string;
  fiscalYearLabel: string;
  isOpen: boolean;
  loading: boolean;
  onSubmit: (options: RegenerateRdProjectDetailsOptions, projectCount: number) => Promise<void>;
  setIsOpen: (isOpen: boolean) => void;
}): ReactElement => {
  const [options, setOptions] = useState<RegenerateRdProjectDetailsOptions>(DEFAULT_OPTIONS);
  const { count, loading: countLoading } = useRdProjectCountForDetailsUpdate({
    fiscalYearId,
    scope: options.scope,
    skip: !isOpen,
  });

  // Each opening starts from the defaults rather than the previous run's choices.
  useEffect(() => {
    if (!isOpen) {
      setOptions(DEFAULT_OPTIONS);
    }
  }, [isOpen]);

  const replacedFields = [
    options.includeName ? 'names' : null,
    options.includeDescription ? 'descriptions' : null,
    options.includeEligibilityDetermination ? 'eligibility' : null,
  ].filter((field): field is string => field !== null);
  const hasNothingToUpdate = count === 0;
  const isSubmitDisabled =
    loading || countLoading || count === undefined || hasNothingToUpdate || replacedFields.length === 0;
  const countLabel = countLoading || count === undefined ? '…' : count;

  return (
    <StyledModal isOpen={isOpen} setIsOpen={setIsOpen}>
      <ModalHeader
        icon={<AlarmBellRing width={24} color={colors.blue[600]} />}
        iconBgColor={colors.blue[50]}
        title={
          <Typography fontSize="base" fontWeight="semibold">
            Update Project Details
          </Typography>
        }
        copy={
          <>
            Re-run Neo.Tax&apos;s AI for {getScopePhrase(options.scope, <strong>{countLabel}</strong>)} in{' '}
            <strong>FY{fiscalYearLabel}</strong>.{' '}
            {replacedFields.length > 0
              ? `Existing ${formatArrayToCommaAndString(replacedFields)} will be replaced.`
              : 'Choose at least one thing to update.'}{' '}
            This job executes in the background; the projects will be locked and not editable while it is running.
          </>
        }
      />
      <Stack direction="column" gap={4} className="mt-5 text-left">
        <RadioGroupCards<RdProjectDetailsUpdateScope>
          id="update-details-scope"
          label="Which projects"
          options={SCOPE_OPTIONS}
          value={options.scope}
          onChange={(scope): void => setOptions((current) => ({ ...current, scope }))}
        />
        <Stack direction="column" gap={2}>
          <Typography fontSize="sm" fontWeight="medium">
            What to update
          </Typography>
          <Stack gap={6}>
            <Checkbox
              id="update-details-name"
              name="includeName"
              title="Name"
              checked={options.includeName}
              onChange={(includeName): void => setOptions((current) => ({ ...current, includeName }))}
            />
            <Checkbox
              id="update-details-description"
              name="includeDescription"
              title="Description"
              checked={options.includeDescription}
              onChange={(includeDescription): void => setOptions((current) => ({ ...current, includeDescription }))}
            />
            <Checkbox
              id="update-details-eligibility"
              name="includeEligibilityDetermination"
              title="Eligibility"
              checked={options.includeEligibilityDetermination}
              onChange={(includeEligibilityDetermination): void =>
                setOptions((current) => ({ ...current, includeEligibilityDetermination }))
              }
            />
          </Stack>
        </Stack>
        {hasNothingToUpdate && (
          <Typography fontSize="sm" color={colors.gray[500]}>
            There are no projects to update for this choice.
          </Typography>
        )}
      </Stack>
      <ModalActions hasTopBorder={false}>
        <Button variant="white" rounded="regular" size="base" disabled={loading} onClick={(): void => setIsOpen(false)}>
          Cancel
        </Button>
        <Button
          variant="primary"
          rounded="regular"
          size="base"
          disabled={isSubmitDisabled}
          onClick={(): Promise<void> => onSubmit(options, count ?? 0)}
        >
          {loading ? 'Starting…' : `Update ${countLabel} project${count === 1 ? '' : 's'}`}
        </Button>
      </ModalActions>
    </StyledModal>
  );
};
